"use client";

import { c as _c } from "react/compiler-runtime";
import { Fragment, type ReactNode, useCallback, useMemo, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { FiCheckCircle, FiMessageSquare } from "react-icons/fi";
import { DoseStatusBadge } from "@/components/dose/detailed-dose-table";
import { DoseDropdown } from "@/components/dose/dose-dropdown";
import type { DoseFormValues } from "@/components/dose/dose-form/dose-form-schema";
import EditDose from "@/components/dose/edit-dose";
import NeedsSignOff from "@/components/dose/needs-sign-off";
import SignOffDoseModal from "@/components/dose/sign-off-dose-modal";
import Pagination from "@/components/pagination";
import { Badge, Button, Tooltip } from "@/components/ui";
import { ButtonGroup } from "@/components/ui/button-group";
import IdDisplay from "@/components/ui/id-display";
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableHeaderRow, TableRow } from "@/components/ui/table";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { getDoseComponentDilutionLabel } from "@/lib/dilution";
import { type MedicationDayTotal, formatDoseComponentLots, formatDoseCount, formatMedicationTotalAmount, getDoseComponentName, getDoseComponents, isSingleMedicationDose, layoutDoseRowsByMedication } from "@/lib/dose-components";
import { type DoseDateGroup, doseSiteLabels, groupDosesByDate, siteOptions, usedValues } from "@/lib/dose-log-table";
import { formatDate, formatStaffName, formatUnit, toClinicDateKey } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { Unit } from "@/types/enums/unit";
import type { DoseComponent, MedicationDilution, MedicationSummary } from "@/types/medication";
import type { NoteDetail, NoteListItem } from "@/types/notes";
import type { PatientDetail } from "@/types/patient";
import { AmountCell, DilutionCell, MedicationCell, ReadOnlyCell, SiteCell, TextSuggestCell } from "./dose-log-cells";
import { canEditSiteComponent, editSiteAmount, editSiteComponent, isDoseCheckedOut, siteComponentIndex } from "./site-components";
import { type DoseEdit, useInlineDoseEdit } from "./use-inline-dose-edit";
import { useSignOffMedicationMutation } from "@/api/provider/user/patient/patient-mutation";
const DAYS_PER_PAGE = 5;
const COLUMN_COUNT = 7;

// Offered before a clinic has typed any of its own.
const COMMON_FREQUENCIES = ["Once", "Daily", "Twice daily", "Weekly", "Every 2 weeks", "Monthly", "As needed"];
interface DoseLogTableProps {
  patient: PatientDetail;
  doses: MedicationSummary[];
  selectedNote?: NoteListItem | NoteDetail | null;
  /** Shows values only; no cell opens an editor. */
  readOnly?: boolean;
  emptyMessage?: string;
  /** Start with day headers on. Charting shows one visit, so it starts flat. */
  defaultGroupByDate?: boolean;
  /** Sits at the right end of the toolbar, above the table. */
  actions?: ReactNode;
}
export function DoseLogTable(t0) {
  const $ = _c(85);
  const {
    patient,
    doses,
    selectedNote,
    readOnly: t1,
    emptyMessage: t2,
    defaultGroupByDate: t3,
    actions
  } = t0;
  const readOnly = t1 === undefined ? false : t1;
  const emptyMessage = t2 === undefined ? "No doses recorded yet" : t2;
  const defaultGroupByDate = t3 === undefined ? true : t3;
  const clock = useClinicClock();
  const {
    tenant
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const {
    save,
    medications: catalog
  } = useInlineDoseEdit();
  let t4;
  if ($[0] !== catalog) {
    t4 = new Map(catalog.map(_temp));
    $[0] = catalog;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const dilutionsByMedication = t4;
  const signOff = useSignOffMedicationMutation();
  const [pendingSignOff, setPendingSignOff] = useState(null);
  const [groupByDate, setGroupByDate] = useState(defaultGroupByDate);
  const [page, setPage] = useState(0);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Set();
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const [closedDays, setClosedDays] = useState(t5);
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = new Set();
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const [closedDoses, setClosedDoses] = useState(t6);
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = id => setClosedDoses(previous => {
      const next = new Set(previous);
      if (!next.delete(id)) {
        next.add(id);
      }
      return next;
    });
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const toggleDose = t7;
  let t8;
  if ($[5] !== currentUser || $[6] !== readOnly) {
    t8 = !readOnly && hasPermission(currentUser, PERMISSIONS.PATIENTS_DOSES_EDIT);
    $[5] = currentUser;
    $[6] = readOnly;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const canEdit = t8;
  let t9;
  if ($[8] !== patient.medications) {
    t9 = patient.medications ?? [];
    $[8] = patient.medications;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const allDoses = t9;
  tenant?.faceChartMuscleLabels;
  const t10 = tenant?.faceChartMuscleLabels;
  let t11;
  if ($[10] !== allDoses || $[11] !== t10) {
    t11 = siteOptions(allDoses, t10);
    $[10] = allDoses;
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const sites = t11;
  let t12;
  if ($[13] !== allDoses) {
    const used = usedValues(allDoses, _temp2);
    const seen = new Set(used.map(_temp3));
    t12 = [...used, ...COMMON_FREQUENCIES.filter(value_0 => !seen.has(value_0.toLowerCase()))];
    $[13] = allDoses;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const frequencies = t12;
  let t13;
  if ($[15] !== clock || $[16] !== doses) {
    t13 = groupDosesByDate(doses, _temp4, clock);
    $[15] = clock;
    $[16] = doses;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const groups = t13;
  const pageCount = Math.max(1, Math.ceil(groups.length / DAYS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  let groupKeys;
  let isAllOpen;
  let t14;
  let visibleGroups;
  if ($[18] !== closedDays || $[19] !== groupByDate || $[20] !== groups || $[21] !== safePage) {
    visibleGroups = groupByDate ? groups.slice(safePage * DAYS_PER_PAGE, (safePage + 1) * DAYS_PER_PAGE) : groups;
    groupKeys = visibleGroups.map(_temp5);
    let t15;
    if ($[26] !== closedDays) {
      t15 = key => !closedDays.has(key);
      $[26] = closedDays;
      $[27] = t15;
    } else {
      t15 = $[27];
    }
    isAllOpen = groupKeys.every(t15);
    let t16;
    if ($[28] !== closedDays) {
      t16 = key_0 => closedDays.has(key_0);
      $[28] = closedDays;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    t14 = groupKeys.every(t16);
    $[18] = closedDays;
    $[19] = groupByDate;
    $[20] = groups;
    $[21] = safePage;
    $[22] = groupKeys;
    $[23] = isAllOpen;
    $[24] = t14;
    $[25] = visibleGroups;
  } else {
    groupKeys = $[22];
    isAllOpen = $[23];
    t14 = $[24];
    visibleGroups = $[25];
  }
  const isAllClosed = t14;
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = key_1 => setClosedDays(previous_0 => {
      const next_0 = new Set(previous_0);
      if (!next_0.delete(key_1)) {
        next_0.add(key_1);
      }
      return next_0;
    });
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const toggleDay = t15;
  let t16;
  if ($[31] !== save) {
    t16 = (dose_0, edit, t17) => {
      const editLocations = t17 === undefined ? false : t17;
      save(dose_0, edit, {
        editLocations
      }).catch(_temp6);
    };
    $[31] = save;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const saveDose = t16;
  let t17;
  if ($[33] !== currentUser?.id || $[34] !== patient.id || $[35] !== pendingSignOff || $[36] !== signOff) {
    t17 = async () => {
      if (!pendingSignOff) {
        return;
      }
      await signOff.mutateAsync({
        medicationId: pendingSignOff.id,
        patientId: patient.id,
        currentProviderId: currentUser?.id
      });
      setPendingSignOff(null);
    };
    $[33] = currentUser?.id;
    $[34] = patient.id;
    $[35] = pendingSignOff;
    $[36] = signOff;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const handleSignOff = t17;
  let t18;
  if ($[38] !== canEdit || $[39] !== closedDoses || $[40] !== dilutionsByMedication || $[41] !== frequencies || $[42] !== patient || $[43] !== saveDose || $[44] !== selectedNote || $[45] !== sites) {
    t18 = (dose_1, summary) => <DoseLogRow key={dose_1.id} summary={summary} dose={dose_1} patient={patient} selectedNote={selectedNote} editable={canEdit && !dose_1.dosespotMedicationId} sites={sites} dilutionsByMedication={dilutionsByMedication} frequencies={frequencies} onSave={saveDose} onRequestSignOff={setPendingSignOff} sitesExpanded={!closedDoses.has(dose_1.id)} onToggleSites={() => toggleDose(dose_1.id)} />;
    $[38] = canEdit;
    $[39] = closedDoses;
    $[40] = dilutionsByMedication;
    $[41] = frequencies;
    $[42] = patient;
    $[43] = saveDose;
    $[44] = selectedNote;
    $[45] = sites;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const renderRow = t18;
  let t19;
  if ($[47] !== clock || $[48] !== renderRow) {
    t19 = rows => {
      const {
        rows: ordered,
        headerBefore
      } = layoutDoseRowsByMedication(rows, clock);
      return ordered.map(dose_2 => renderRow(dose_2, headerBefore.get(dose_2.id)));
    };
    $[47] = clock;
    $[48] = renderRow;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const renderRows = t19;
  let t20;
  if ($[50] !== actions || $[51] !== doses.length || $[52] !== groupByDate || $[53] !== groupKeys || $[54] !== isAllClosed || $[55] !== isAllOpen) {
    t20 = (doses.length > 0 || actions) && <div className="flex flex-wrap items-center gap-2">{doses.length > 0 && <ButtonGroup><Button size="sm" variant={groupByDate ? "primary" : "secondary"} aria-pressed={groupByDate} onClick={() => setGroupByDate(true)}>By day</Button><Button size="sm" variant={groupByDate ? "secondary" : "primary"} aria-pressed={!groupByDate} onClick={() => setGroupByDate(false)}>All doses</Button></ButtonGroup>}{doses.length > 0 && groupByDate && groupKeys.length > 1 && <ButtonGroup className={cx(!actions && "ml-auto")}><Button variant="secondary" size="sm" onClick={() => setClosedDays(new Set())} disabled={isAllOpen}>Expand all</Button><Button variant="secondary" size="sm" onClick={() => setClosedDays(new Set(groupKeys))} disabled={isAllClosed}>Collapse all</Button></ButtonGroup>}{actions && <div className="ml-auto">{actions}</div>}</div>;
    $[50] = actions;
    $[51] = doses.length;
    $[52] = groupByDate;
    $[53] = groupKeys;
    $[54] = isAllClosed;
    $[55] = isAllOpen;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <TableHead><TableHeaderRow className="[&>th]:whitespace-nowrap [&>th]:px-2"><TableHeaderCell className="min-w-32">Site</TableHeaderCell><TableHeaderCell className="min-w-32">Medication</TableHeaderCell><TableHeaderCell>Amount</TableHeaderCell><TableHeaderCell className="min-w-32">Reconstitution</TableHeaderCell><TableHeaderCell className="min-w-28">Frequency (Sig)</TableHeaderCell><TableHeaderCell>Lot #</TableHeaderCell><TableHeaderCell className="sticky right-0 w-12 border-l border-border bg-background lg:border-l-0" /></TableHeaderRow></TableHead>;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== closedDays || $[59] !== doses.length || $[60] !== emptyMessage || $[61] !== groupByDate || $[62] !== groups || $[63] !== renderRows || $[64] !== visibleGroups) {
    t22 = doses.length === 0 ? <TableRow selectable={false}><TableCell colSpan={COLUMN_COUNT} className="py-8 text-center text-body text-foreground-secondary">{emptyMessage}</TableCell></TableRow> : groupByDate ? visibleGroups.map(group_0 => {
      const key_2 = group_0.dateKey || "undated";
      const isExpanded = !closedDays.has(key_2);
      return <Fragment key={key_2}><DoseDayRow group={group_0} isExpanded={isExpanded} onToggle={() => toggleDay(key_2)} />{isExpanded && renderRows(group_0.doses)}</Fragment>;
    }) : renderRows(groups.flatMap(_temp7));
    $[58] = closedDays;
    $[59] = doses.length;
    $[60] = emptyMessage;
    $[61] = groupByDate;
    $[62] = groups;
    $[63] = renderRows;
    $[64] = visibleGroups;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== t22) {
    t23 = <div className="w-full overflow-x-auto rounded-lg border border-border bg-background"><Table className="w-full overflow-visible leading-normal">{t21}<TableBody>{t22}</TableBody></Table></div>;
    $[66] = t22;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== groupByDate || $[69] !== groups.length || $[70] !== pageCount || $[71] !== safePage) {
    t24 = groupByDate && groups.length > DAYS_PER_PAGE && <Pagination displayText="days" total={groups.length} currentPage={safePage + 1} perPage={DAYS_PER_PAGE} handlePageChange={direction => setPage(direction === "next" ? Math.min(pageCount - 1, safePage + 1) : Math.max(0, safePage - 1))} />;
    $[68] = groupByDate;
    $[69] = groups.length;
    $[70] = pageCount;
    $[71] = safePage;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  const t25 = !!pendingSignOff;
  let t26;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => setPendingSignOff(null);
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] !== handleSignOff || $[75] !== patient || $[76] !== pendingSignOff || $[77] !== signOff.isPending || $[78] !== t25) {
    t27 = <SignOffDoseModal isOpen={t25} onClose={t26} onConfirm={handleSignOff} medication={pendingSignOff} patient={patient} isLoading={signOff.isPending} />;
    $[74] = handleSignOff;
    $[75] = patient;
    $[76] = pendingSignOff;
    $[77] = signOff.isPending;
    $[78] = t25;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  let t28;
  if ($[80] !== t20 || $[81] !== t23 || $[82] !== t24 || $[83] !== t27) {
    t28 = <div className="space-y-3">{t20}{t23}{t24}{t27}</div>;
    $[80] = t20;
    $[81] = t23;
    $[82] = t24;
    $[83] = t27;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  return t28;
}

/** Heads one medication's doses for a day, with their total. */
function _temp7(group_1) {
  return group_1.doses;
}
function _temp6() {}
function _temp5(group) {
  return group.dateKey || "undated";
}
function _temp4(iso) {
  return formatDate(iso, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}
function _temp3(value) {
  return value.toLowerCase();
}
function _temp2(dose) {
  return dose.frequency;
}
function _temp(medication) {
  return [medication.id, {
    dilutions: medication.dilutions ?? [],
    unit: medication.unit
  }] as const;
}
function DoseLogSummaryRow(t0) {
  const $ = _c(17);
  const {
    total
  } = t0;
  let t1;
  if ($[0] !== total.name) {
    t1 = <span className="text-body-strong text-foreground">{total.name}</span>;
    $[0] = total.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== total.doseCount) {
    t2 = formatDoseCount(total.doseCount);
    $[2] = total.doseCount;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <Badge>{t2}</Badge>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <TableCell colSpan={2} className="whitespace-normal px-2"><span className="flex items-center gap-2">{t1}{t3}</span></TableCell>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== total) {
    t5 = formatMedicationTotalAmount(total);
    $[9] = total;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <TableCell className="whitespace-nowrap px-2 text-body-strong tabular-nums text-foreground">{t5}</TableCell>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TableCell colSpan={COLUMN_COUNT - 3} />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t4 || $[15] !== t6) {
    t8 = <TableRow selectable={false} className="bg-background-secondary">{t4}{t6}{t7}</TableRow>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}

/** One day: what was given that day at a glance, and the toggle for its rows. */
function DoseDayRow(t0) {
  const $ = _c(30);
  const {
    group,
    isExpanded,
    onToggle
  } = t0;
  const count = group.doses.length;
  let t1;
  if ($[0] !== group.doses) {
    t1 = group.doses.filter(_temp8);
    $[0] = group.doses;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const unsigned = t1.length;
  let t2;
  if ($[2] !== group.doses) {
    t2 = Array.from(new Set(group.doses.flatMap(_temp9)));
    $[2] = group.doses;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const medications = t2;
  const t3 = `${isExpanded ? "Collapse" : "Expand"} ${group.label}`;
  const t4 = !isExpanded && "-rotate-90";
  let t5;
  if ($[4] !== t4) {
    t5 = cx("h-3 w-3 transition-transform duration-150", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <FaChevronDown className={t5} />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isExpanded || $[9] !== t3 || $[10] !== t6) {
    t7 = <button type="button" className="flex h-6 w-6 items-center justify-center rounded text-foreground-secondary hover:text-foreground" aria-expanded={isExpanded} aria-label={t3}>{t6}</button>;
    $[8] = isExpanded;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== group.label) {
    t8 = <span className="whitespace-nowrap font-semibold text-foreground">{group.label}</span>;
    $[12] = group.label;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = `${count} ${count === 1 ? "dose" : "doses"}`;
  let t10;
  if ($[14] !== t9) {
    t10 = <span className="whitespace-nowrap text-caption text-foreground-muted">{t9}</span>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== isExpanded || $[17] !== medications) {
    t11 = !isExpanded && medications.length > 0 && <span className="min-w-0 truncate text-caption text-foreground-secondary">{medications.join(" \xB7 ")}</span>;
    $[16] = isExpanded;
    $[17] = medications;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== unsigned) {
    t12 = unsigned > 0 && <Badge variant="warning" className="ml-auto">{unsigned} unsigned</Badge>;
    $[19] = unsigned;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t12 || $[24] !== t7 || $[25] !== t8) {
    t13 = <TableCell colSpan={COLUMN_COUNT} className="whitespace-normal"><span className="flex flex-wrap items-center gap-x-2 gap-y-1">{t7}{t8}{t10}{t11}{t12}</span></TableCell>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t7;
    $[25] = t8;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== onToggle || $[28] !== t13) {
    t14 = <TableRow selectable={false} className="cursor-pointer bg-background-secondary transition-colors hover:bg-hover" onClick={onToggle}>{t13}</TableRow>;
    $[27] = onToggle;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}
function _temp9(dose_0) {
  return getDoseComponents(dose_0).map(getDoseComponentName);
}
function _temp8(dose) {
  return !dose.signedOffDate && !dose.dosespotMedicationId;
}
interface DoseLogRowProps {
  dose: MedicationSummary;
  patient: PatientDetail;
  selectedNote?: NoteListItem | NoteDetail | null;
  editable: boolean;
  sites: string[];
  dilutionsByMedication: ReadonlyMap<string, {
    dilutions: MedicationDilution[];
    unit?: string | null;
  }>;
  frequencies: string[];
  onSave: (dose: MedicationSummary, edit: DoseEdit, editLocations?: boolean) => void;
  onRequestSignOff: (dose: MedicationSummary) => void;
  /** The day's total for this medication, shown in a row above this dose. */
  summary?: MedicationDayTotal;
  /** A dose given at several sites shows a row per site under it. */
  sitesExpanded: boolean;
  onToggleSites: () => void;
}
function DoseLogRow(t0) {
  const $ = _c(89);
  const {
    dose,
    patient,
    selectedNote,
    editable,
    sites,
    dilutionsByMedication,
    frequencies,
    onSave,
    onRequestSignOff,
    summary,
    sitesExpanded,
    onToggleSites
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== dilutionsByMedication || $[1] !== dose || $[2] !== editable || $[3] !== frequencies || $[4] !== onRequestSignOff || $[5] !== onSave || $[6] !== onToggleSites || $[7] !== patient || $[8] !== selectedNote || $[9] !== sites || $[10] !== sitesExpanded || $[11] !== summary) {
    const components = getDoseComponents(dose);
    const component = components.length === 1 ? components[0] : undefined;
    const editsComponent = editable && !!component && !dose.compoundMedicationId;
    const singleMedication = isSingleMedicationDose(dose);
    const isSplit = singleMedication && components.length > 1;
    let t4;
    if ($[15] !== dose) {
      t4 = isDoseCheckedOut(dose);
      $[15] = dose;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    const checkedOut = t4;
    const t5 = dose.doseDate ?? dose.createdDate;
    let t6;
    if ($[17] !== t5) {
      t6 = toClinicDateKey(t5);
      $[17] = t5;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    const administeredDay = t6;
    const catalogEntry = dilutionsByMedication.get(components[0]?.medicationId ?? "");
    const siteLabels = doseSiteLabels(dose);
    const siteRows = dose.locations ?? [];
    const amounts = new Map(siteRows.map(_temp10));
    const hasSiteRows = siteRows.length > 1;
    const showSiteRows = hasSiteRows && sitesExpanded;
    const unit = singleMedication ? components[0].unit as Unit | null | undefined : undefined;
    const medicationName = [...new Set(components.map(getDoseComponentName))].join(" + ");
    const dilutionLabel = [...new Set(components.map(getDoseComponentDilutionLabel).filter(Boolean))].join(", ");
    const doseLots = components.map(formatDoseComponentLots).filter(Boolean).join("; ");
    const totalAmount = components.reduce(_temp11, 0);
    if ($[19] !== summary) {
      t1 = summary && <DoseLogSummaryRow total={summary} />;
      $[19] = summary;
      $[20] = t1;
    } else {
      t1 = $[20];
    }
    let t7;
    if ($[21] !== hasSiteRows || $[22] !== onToggleSites || $[23] !== sitesExpanded) {
      t7 = hasSiteRows && <button type="button" className="mt-1 flex size-6 shrink-0 items-center justify-center rounded text-foreground-secondary hover:bg-hover hover:text-foreground" aria-expanded={sitesExpanded} aria-label={`${sitesExpanded ? "Collapse" : "Expand"} sites`} onClick={onToggleSites}><FaChevronDown className={cx("size-3 transition-transform duration-150", !sitesExpanded && "-rotate-90")} /></button>;
      $[21] = hasSiteRows;
      $[22] = onToggleSites;
      $[23] = sitesExpanded;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    const t8 = editable ? <SiteCell value={siteLabels} options={sites} summary={showSiteRows ? `${siteRows.length} sites` : undefined} onSave={labels => onSave(dose, values => ({
      ...values,
      locations: labels.map(label => ({
        label,
        amount: amounts.get(label) != null ? String(amounts.get(label)) : ""
      }))
    }), true)} /> : <ReadOnlyCell isEmpty={siteLabels.length === 0}>{showSiteRows ? <span className="text-foreground-muted">{`${siteRows.length} sites`}</span> : siteLabels.join(", ")}</ReadOnlyCell>;
    let t9;
    if ($[25] !== t8) {
      t9 = <div className="min-w-0 flex-1">{t8}</div>;
      $[25] = t8;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    let t10;
    if ($[27] !== t7 || $[28] !== t9) {
      t10 = <TableCell className="whitespace-normal"><div className="flex items-start">{t7}{t9}</div></TableCell>;
      $[27] = t7;
      $[28] = t9;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    const t11 = editable && singleMedication ? <MedicationCell value={components[0].name} onSave={medication => onSave(dose, values_0 => ({
      ...values_0,
      components: values_0.components.map(current => ({
        ...current,
        name: medication.name,
        medicationId: medication.id,
        unit: medication.unit as Unit | null || current.unit,
        lotSelection: null
      }))
    }))} /> : <ReadOnlyCell><span className="font-medium text-foreground">{medicationName}</span></ReadOnlyCell>;
    let t12;
    if ($[30] !== dose.provider) {
      t12 = dose.provider ? formatStaffName(dose.provider) : "No provider";
      $[30] = dose.provider;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    let t13;
    if ($[32] !== t12) {
      t13 = <span className="text-caption text-foreground-secondary">{t12}</span>;
      $[32] = t12;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    let t14;
    if ($[34] !== dose.signedOffDate) {
      t14 = dose.signedOffDate && <Tooltip content={`Signed off ${formatDate(dose.signedOffDate)}`} triggerAsChild={true} triggerOnlyHover={true}><span className="inline-flex items-center gap-0.5 text-caption text-status-success-text"><FiCheckCircle className="size-3" /><span>Signed</span></span></Tooltip>;
      $[34] = dose.signedOffDate;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    let t15;
    if ($[36] !== dose || $[37] !== onRequestSignOff) {
      t15 = <NeedsSignOff medication={dose} onRequestSignOff={onRequestSignOff} />;
      $[36] = dose;
      $[37] = onRequestSignOff;
      $[38] = t15;
    } else {
      t15 = $[38];
    }
    let t16;
    if ($[39] !== dose.compoundMedicationId) {
      t16 = dose.compoundMedicationId && <Badge variant="info">Compound</Badge>;
      $[39] = dose.compoundMedicationId;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    let t17;
    if ($[41] !== dose.dosespotMedicationId) {
      t17 = dose.dosespotMedicationId && <Badge variant="info">E-prescribed</Badge>;
      $[41] = dose.dosespotMedicationId;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    let t18;
    if ($[43] !== dose.status) {
      t18 = <DoseStatusBadge status={dose.status} />;
      $[43] = dose.status;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    let t19;
    if ($[45] !== dose.id) {
      t19 = <IdDisplay id={dose.id} className="text-caption text-foreground-muted" />;
      $[45] = dose.id;
      $[46] = t19;
    } else {
      t19 = $[46];
    }
    let t20;
    if ($[47] !== t13 || $[48] !== t14 || $[49] !== t15 || $[50] !== t16 || $[51] !== t17 || $[52] !== t18 || $[53] !== t19) {
      t20 = <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 px-2 pb-1">{t13}{t14}{t15}{t16}{t17}{t18}{t19}</div>;
      $[47] = t13;
      $[48] = t14;
      $[49] = t15;
      $[50] = t16;
      $[51] = t17;
      $[52] = t18;
      $[53] = t19;
      $[54] = t20;
    } else {
      t20 = $[54];
    }
    let t21;
    if ($[55] !== t11 || $[56] !== t20) {
      t21 = <TableCell className="whitespace-normal">{t11}{t20}</TableCell>;
      $[55] = t11;
      $[56] = t20;
      $[57] = t21;
    } else {
      t21 = $[57];
    }
    const t22 = editsComponent ? <AmountCell amount={component.amount} unit={component.unit as Unit | null} quantity={component.quantity} onSave={amount => onSave(dose, values_1 => ({
      ...values_1,
      components: values_1.components.map(current_0 => ({
        ...current_0,
        amount
      }))
    }))} /> : isSplit ? <ReadOnlyCell><span className="whitespace-nowrap tabular-nums">{`${Math.round(totalAmount * 1000000) / 1000000} ${unit ? formatUnit(unit) : ""}`}</span></ReadOnlyCell> : <ReadOnlyCell>{components.map(_temp12)}</ReadOnlyCell>;
    let t23;
    if ($[58] !== t22) {
      t23 = <TableCell className="whitespace-normal">{t22}</TableCell>;
      $[58] = t22;
      $[59] = t23;
    } else {
      t23 = $[59];
    }
    const t24 = editsComponent && catalogEntry?.dilutions.length ? <DilutionCell dilutions={catalogEntry.dilutions} value={component.dilutionId} unit={catalogEntry.unit} onSave={dilutionId => onSave(dose, values_2 => ({
      ...values_2,
      components: values_2.components.map(current_1 => ({
        ...current_1,
        dilutionId
      }))
    }))} /> : components.some(_temp13) ? <ReadOnlyCell><span className="tabular-nums">{[...new Set(components.map(getDoseComponentDilutionLabel).filter(Boolean))].join(", ")}</span></ReadOnlyCell> : <ReadOnlyCell isEmpty={true} />;
    let t25;
    if ($[60] !== t24) {
      t25 = <TableCell className="whitespace-normal">{t24}</TableCell>;
      $[60] = t24;
      $[61] = t25;
    } else {
      t25 = $[61];
    }
    let t26;
    if ($[62] !== dose || $[63] !== editable || $[64] !== frequencies || $[65] !== onSave) {
      t26 = <TableCell className="whitespace-normal">{editable ? <TextSuggestCell label="frequency" value={dose.frequency ?? ""} suggestions={frequencies} placeholder="Add sig" inputPlaceholder="E.g. Daily, Every 12 weeks" onSave={frequency => onSave(dose, values_3 => ({
          ...values_3,
          frequency
        }))} /> : <ReadOnlyCell isEmpty={!dose.frequency}>{dose.frequency}</ReadOnlyCell>}</TableCell>;
      $[62] = dose;
      $[63] = editable;
      $[64] = frequencies;
      $[65] = onSave;
      $[66] = t26;
    } else {
      t26 = $[66];
    }
    const t27 = editsComponent ? <EditLotsButton dose={dose} part={component} administeredDay={administeredDay} ariaLabel="Edit lots" /> : <ReadOnlyCell isEmpty={!doseLots}>{components.filter(_temp14).map(_temp15)}</ReadOnlyCell>;
    let t28;
    if ($[67] !== t27) {
      t28 = <TableCell className="whitespace-normal">{t27}</TableCell>;
      $[67] = t27;
      $[68] = t28;
    } else {
      t28 = $[68];
    }
    let t29;
    if ($[69] !== dose || $[70] !== patient || $[71] !== selectedNote) {
      t29 = <TableCell className="sticky right-0 w-12 border-l border-border bg-background lg:border-l-0"><DoseDropdown patient={patient} selectedNote={selectedNote} medication={dose} /></TableCell>;
      $[69] = dose;
      $[70] = patient;
      $[71] = selectedNote;
      $[72] = t29;
    } else {
      t29 = $[72];
    }
    if ($[73] !== t10 || $[74] !== t21 || $[75] !== t23 || $[76] !== t25 || $[77] !== t26 || $[78] !== t28 || $[79] !== t29) {
      t2 = <TableRow selectable={false} className="group/row align-top">{t10}{t21}{t23}{t25}{t26}{t28}{t29}</TableRow>;
      $[73] = t10;
      $[74] = t21;
      $[75] = t23;
      $[76] = t25;
      $[77] = t26;
      $[78] = t28;
      $[79] = t29;
      $[80] = t2;
    } else {
      t2 = $[80];
    }
    t3 = showSiteRows && siteRows.map((site, index_1) => {
      const sitePart = singleMedication ? components[siteComponentIndex(components, site.doseComponentId)] : undefined;
      const editsSitePart = editable && !!sitePart && canEditSiteComponent(dose, index_1);
      const siteDilutionLabel = sitePart ? getDoseComponentDilutionLabel(sitePart) : dilutionLabel;
      const ownsPart = !!sitePart && siteRows.filter(other => components[siteComponentIndex(components, other.doseComponentId)] === sitePart).length === 1;
      const siteLots = sitePart ? formatDoseComponentLots(sitePart) : doseLots;
      const siteHint = editable && !!sitePart && !editsSitePart ? checkedOut ? "This dose was checked out, so its reconstitution stays as sold" : "Add an amount at this site to give it its own reconstitution" : null;
      const saveSitePart = change => onSave(dose, values_4 => editSiteComponent(values_4, index_1, change), true);
      return <TableRow key={site.id ?? `${site.label}-${index_1}`} selectable={false} className="bg-background-secondary/40 [&>td+td]:border-l [&>td]:border-t [&>td]:border-border"><TableCell className="whitespace-normal"><ReadOnlyCell><span className="pl-6 text-foreground-secondary">{site.label}</span></ReadOnlyCell></TableCell><TableCell className="whitespace-normal"><ReadOnlyCell>{medicationName}</ReadOnlyCell></TableCell><TableCell className="whitespace-normal">{editable ? <AmountCell amount={site.amount ?? undefined} unit={unit} ariaLabel={`Edit amount at ${site.label}`} onSave={amount_0 => onSave(dose, values_5 => editSiteAmount(values_5, index_1, amount_0, {
            adjustsPart: isSplit && !checkedOut
          }), true)} /> : <ReadOnlyCell isEmpty={site.amount == null}><span className="whitespace-nowrap tabular-nums">{`${site.amount} ${unit ? formatUnit(unit) : ""}`}</span></ReadOnlyCell>}</TableCell><TableCell className="whitespace-normal">{editsSitePart && catalogEntry?.dilutions.length ? <DilutionCell dilutions={catalogEntry.dilutions} value={sitePart.dilutionId} unit={catalogEntry.unit} ariaLabel={`Edit reconstitution at ${site.label}`} onSave={dilutionId_0 => saveSitePart(part_4 => ({
            ...part_4,
            dilutionId: dilutionId_0
          }))} /> : <SiteHint hint={siteHint}><ReadOnlyCell isEmpty={!siteDilutionLabel}><span className="tabular-nums">{siteDilutionLabel}</span></ReadOnlyCell></SiteHint>}</TableCell><TableCell /><TableCell colSpan={2} className="whitespace-normal">{editable && sitePart ? <EditLotsButton dose={dose} part={sitePart} administeredDay={administeredDay} ariaLabel={`Edit lots at ${site.label}`} /> : <ReadOnlyCell isEmpty={!siteLots}>{siteLots && <>{!ownsPart && <span className="text-caption text-foreground-secondary">Dose lots (not recorded by site):{" "}</span>}<span className="whitespace-nowrap font-mono text-caption-strong">{siteLots}</span></>}</ReadOnlyCell>}</TableCell></TableRow>;
    });
    $[0] = dilutionsByMedication;
    $[1] = dose;
    $[2] = editable;
    $[3] = frequencies;
    $[4] = onRequestSignOff;
    $[5] = onSave;
    $[6] = onToggleSites;
    $[7] = patient;
    $[8] = selectedNote;
    $[9] = sites;
    $[10] = sitesExpanded;
    $[11] = summary;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
  }
  let t4;
  if ($[81] !== dose.comment || $[82] !== dose.sideEffects) {
    t4 = (dose.sideEffects || dose.comment) && <TableRow selectable={false} className="!border-t-0"><TableCell colSpan={COLUMN_COUNT} className="space-y-1 whitespace-normal pt-0 text-caption text-foreground-secondary">{dose.sideEffects && <div className="break-words px-2 italic text-muted-foreground"><span className="font-medium not-italic">Side effects:</span>{" "}<span>{dose.sideEffects}</span></div>}{dose.comment && <span className="inline-flex items-start gap-1.5 px-2"><FiMessageSquare className="mt-0.5 size-3 shrink-0" /><span className="break-words">{dose.comment}</span></span>}</TableCell></TableRow>;
    $[81] = dose.comment;
    $[82] = dose.sideEffects;
    $[83] = t4;
  } else {
    t4 = $[83];
  }
  let t5;
  if ($[84] !== t1 || $[85] !== t2 || $[86] !== t3 || $[87] !== t4) {
    t5 = <>{t1}{t2}{t3}{t4}</>;
    $[84] = t1;
    $[85] = t2;
    $[86] = t3;
    $[87] = t4;
    $[88] = t5;
  } else {
    t5 = $[88];
  }
  return t5;
}

/** Why a site's lot and reconstitution can't be set yet. */
/** A part's lots, picked with their amounts in the Edit dose window. */
function _temp15(part_3, index_0) {
  return <div key={part_3.id ?? index_0} className="whitespace-nowrap font-mono text-caption-strong">{formatDoseComponentLots(part_3)}</div>;
}
function _temp14(part_2) {
  return formatDoseComponentLots(part_2);
}
function _temp13(part_1) {
  return part_1.diluentVolume;
}
function _temp12(part_0, index) {
  return <div key={part_0.id ?? index} className="whitespace-nowrap tabular-nums">{`${part_0.amount ?? "\u2014"} ${part_0.unit ? formatUnit(part_0.unit as Unit) : ""}`}</div>;
}
function _temp11(sum, part) {
  return sum + (part.amount ?? 0) * (part.quantity ?? 1);
}
function _temp10(row) {
  return [row.label, row.amount] as const;
}
function EditLotsButton(t0) {
  const $ = _c(29);
  const {
    dose,
    part,
    administeredDay,
    ariaLabel
  } = t0;
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== administeredDay || $[1] !== ariaLabel || $[2] !== dose || $[3] !== part) {
    const expiredLotDate = part.lots?.map(_temp16).filter(expiry => !!expiry && expiry < administeredDay).sort()[0];
    T1 = EditDose;
    t7 = dose;
    T0 = Button;
    t1 = "ghost";
    t2 = "sm";
    t3 = ariaLabel;
    t4 = "h-auto whitespace-normal text-left font-mono";
    let t8;
    if ($[13] !== part) {
      t8 = formatDoseComponentLots(part) || "Choose lots";
      $[13] = part;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    if ($[15] !== t8) {
      t5 = <span>{t8}</span>;
      $[15] = t8;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    t6 = expiredLotDate && <span className="ml-1 text-status-error-text" title={`Expired before this dose was given: ${formatDate(expiredLotDate)}`}>Expired</span>;
    $[0] = administeredDay;
    $[1] = ariaLabel;
    $[2] = dose;
    $[3] = part;
    $[4] = T0;
    $[5] = T1;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    T0 = $[4];
    T1 = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
  }
  let t8;
  if ($[17] !== T0 || $[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t8 = <T0 variant={t1} size={t2} aria-label={t3} className={t4}>{t5}{t6}</T0>;
    $[17] = T0;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== T1 || $[26] !== t7 || $[27] !== t8) {
    t9 = <T1 medication={t7}>{t8}</T1>;
    $[25] = T1;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
function _temp16(lot) {
  return lot.expiryDate;
}
function SiteHint(t0) {
  const $ = _c(7);
  const {
    hint,
    children
  } = t0;
  if (!hint) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children) {
    t1 = <div>{children}</div>;
    $[2] = children;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== hint || $[5] !== t1) {
    t2 = <Tooltip content={hint} triggerAsChild={true} triggerOnlyHover={true}>{t1}</Tooltip>;
    $[4] = hint;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}